import type { ClientMetadata, DeployBlock, InactiveTag } from '@/types';
import { formatNzDate } from './relativeTime';

/** Reads the two tags (do not deploy, inactive) off a set of client rows. */
export interface FlagReader {
  /** True when the client is tagged inactive. */
  isInactive: (clientName: string) => boolean;
  /** The inactive tag: who set it, when, and the reason if one was given. */
  inactive: (clientName: string) => InactiveTag | undefined;
  /** The do-not-deploy tag: the reason, who set it and when. Undefined means deploys are allowed. */
  deployBlock: (clientName: string) => DeployBlock | undefined;
  /** The client's whole row: its contract, partner and trial dates. Undefined when it has none. */
  row: (clientName: string) => ClientMetadata | undefined;
}

/**
 * A tag is on while its attribute is on the row, whatever the attribute
 * holds. A row edited by hand can lack a field, so each one is filled in here
 * and no caller has to guard against a half-written tag.
 */
export function flagReader(metadata: ReadonlyMap<string, ClientMetadata>): FlagReader {
  const inactive = (clientName: string): InactiveTag | undefined => {
    const tag = metadata.get(clientName)?.inactive as Partial<InactiveTag> | undefined;
    if (!tag) return undefined;
    const reason = typeof tag.reason === 'string' ? tag.reason.trim() : '';
    return { ...(reason ? { reason } : {}), setBy: tag.setBy || 'unknown', setAt: tag.setAt || '' };
  };
  const deployBlock = (clientName: string): DeployBlock | undefined => {
    const tag = metadata.get(clientName)?.deployBlock as Partial<DeployBlock> | undefined;
    if (!tag) return undefined;
    const reason = typeof tag.reason === 'string' ? tag.reason.trim() : '';
    return { reason: reason || 'No reason was given', setBy: tag.setBy || 'unknown', setAt: tag.setAt || '' };
  };
  return {
    isInactive: (clientName) => Boolean(metadata.get(clientName)?.inactive),
    inactive,
    deployBlock,
    row: (clientName) => metadata.get(clientName),
  };
}

/** "nathan" from "nathan@arcanum.ai". */
export function shortPerson(email?: string): string {
  return email ? email.split('@')[0] : '';
}

/** A reason as a sentence of its own: it ends with a full stop exactly once. */
export function asSentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** "Set by nathan on 13 Oct 2026". The date is left out when the tag has none. */
export function setByLine(tag: { setBy: string; setAt: string }): string {
  const when = formatNzDate(tag.setAt);
  return `Set by ${shortPerson(tag.setBy) || 'an unknown person'}${when ? ` on ${when}` : ''}`;
}

/** The hover text for a "Do not deploy" tag: the reason, then who set it and when. */
export function deployBlockTitle(block: DeployBlock): string {
  return `${asSentence(block.reason)} ${setByLine(block)}.`;
}

/** The hover text for an "Inactive" tag. */
export function inactiveTitle(tag: InactiveTag): string {
  const when = formatNzDate(tag.setAt);
  const since = `Inactive${when ? ` since ${when}` : ''}, set by ${shortPerson(tag.setBy) || 'an unknown person'}.`;
  return tag.reason ? `${since} ${asSentence(tag.reason)}` : since;
}

/** A client a group run left out, and why. This is what the group page shows afterwards. */
export interface SkippedClient {
  clientName: string;
  reason: string;
  setBy: string;
  setAt?: string;
}
